import React, { useState, useEffect } from "react";
import "./CreateDevis.css";
import "bootstrap/dist/css/bootstrap.min.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { useParams } from "react-router-dom";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import { 
  Plus, 
  Trash2, 
  Download, 
  Send, 
  FileText, 
  User, 
  Mail,
  Phone,
  Package, 
  DollarSign,
  CheckCircle2,
  Search,
  Calculator,
  Loader2
} from 'lucide-react';

declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface Article {
  id: number;
  ref?: string;
  name: string;
  quantity: number;
  price: number;
  vendeur?: string;
}

const CreateDevisDirect: React.FC = () => {
  const { id, idmagasin } = useParams<{ id?: string; idmagasin?: string }>();
  const [clientName, setClientName] = useState<string>("");
  const [numeroTele, setNumeroTele] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [articles, setArticles] = useState<Article[]>([
    { id: 1, name: "", quantity: 1, price: 0 }
  ]);
  const [Refs, setRef] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [globalCommissionPct, setGlobalCommissionPct] = useState<number>(0);
  
  const MagasinName = localStorage.getItem("MagasinName") ?? "Magasin";
  const MagasinId = id || idmagasin || localStorage.getItem("MagasinId");

  const handleAddArticle = (e: React.FormEvent) => {
    e.preventDefault();
    const newId = articles.length + 1;
    setArticles([...articles, { id: newId, name: "", quantity: 1, price: 0 }]);
  };

  const handleRemoveArticle = (index: number) => {
    if (articles.length > 1) {
      const updatedArticles = articles.filter((_, i) => i !== index);
      setArticles(updatedArticles);
    }
  };

  const handleChangeArticleValue = (index: number, field: string, value: string | number) => {
    const updatedArticles = [...articles];
    let cleanValue: number | string = value;

    if (field !== "name") {
      cleanValue = Number(String(value).replace(",", "."));
      if (isNaN(cleanValue)) cleanValue = 0;
    }

    updatedArticles[index] = {
      ...updatedArticles[index],
      [field]: field === "name" ? value : cleanValue,
    };
    setArticles(updatedArticles);
  };

  const handleSelectArticle = (index: number, selectedArticle: any) => {
    const updatedArticles = [...articles];
    const basePrice = Number(String(selectedArticle.PrixNetHT).replace(",", ".")) || 0;
    const sameMagasin = selectedArticle.NomMagasin && MagasinName && selectedArticle.NomMagasin.toLowerCase() === MagasinName.toLowerCase();
    const priceToSet = sameMagasin ? basePrice : basePrice * (1 + (globalCommissionPct / 100));
    updatedArticles[index] = {
      ...updatedArticles[index],
      name: selectedArticle.Designation,
      quantity: 1,
      price: Number(priceToSet.toFixed(2)),
      ref: selectedArticle.RefArticle,
      vendeur: selectedArticle.NomMagasin || MagasinName || "",
    };
    setArticles(updatedArticles);
    setSearch("");
    setShowSuggestions(false);
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/commission`)
      .then(r => {
        const rules = r.data || [];
        const glob = rules.find((x: any) => x.commission_type === 'global');
        if (glob) setGlobalCommissionPct(parseFloat(glob.commission_value) || 0);
      })
      .catch(() => setGlobalCommissionPct(0));
  }, []);

  const getTotalPerArticle = (article: Article) => {
    const quantity = Number(article.quantity) || 0;
    const price = Number(article.price) || 0;
    return quantity * price;
  };

  const totalAllArticles = articles.reduce(
    (acc, article) => acc + getTotalPerArticle(article),
    0
  );

  const totalTVA = totalAllArticles * 0.2;
  const totalTTC = totalAllArticles + totalTVA;

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (e.target.value.trim() === "") {
      setRef([]);
      setShowSuggestions(false);
    }
  };

  const triggerSearch = () => {
    const val = search.trim();
    if (val === "") {
      setRef([]);
      setShowSuggestions(false);
      return;
    }
    getRef(val);
  };

  const getRef = async (searchTerm: string) => {
    try {
      if (searchTerm.trim() === "") {
        setRef([]);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/search-articles`,
        {
          params: { q: searchTerm },
        }
      );
      setRef(response.data.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString();

    doc.setFontSize(10);
    const uniqueClientCode = `CLT-${Date.now()}`;
    doc.text(`Code Client : ${uniqueClientCode}`, 14, 10);
    doc.text(`Date : ${today}`, 160, 10, { align: "right" });
    const now = new Date();
    const uniqueDevisNumber = `PD${now.getFullYear().toString().slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    doc.text(`Devis N° ${uniqueDevisNumber}`, 105, 20, { align: "center" });
    doc.text(`Le nom de Magasin : ${MagasinName || "Non défini"}`, 140, 35);    
    doc.line(10, 38, 200, 38);

    const tableColumn = ["Réf.", "Désignation", "Qté", "P.U HT", "T.V.A", "P.U TTC", "Montant TTC"];
    const tableRows: (string | number)[][] = [];

    articles.forEach((article, index) => {
      const tva = 0.2;
      const pu_ht = article.price;
      const pu_ttc = pu_ht * (1 + tva);
      const montant = article.quantity * pu_ttc;
      tableRows.push([
        article.ref ? article.ref : "ref",
        article.name,
        article.quantity,
        pu_ht.toFixed(2),
        "20%",
        pu_ttc.toFixed(2),
        montant.toFixed(2),
      ]);
    });

    autoTable(doc, {
      startY: 45,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [211, 211, 211], textColor: [0, 0, 0], halign: "center" },
      columnStyles: {
        0: { cellWidth: 20 }, 1: { cellWidth: 60 }, 2: { cellWidth: 15 },
        3: { cellWidth: 20 }, 4: { cellWidth: 15 }, 5: { cellWidth: 20 }, 6: { cellWidth: 25 },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 5;
    const totalHT = articles.reduce((sum, a) => sum + (a.price * a.quantity), 0);
    const totalTVA = totalHT * 0.2;
    const totalTTC = totalHT + totalTVA;

    doc.text(`Total H.T : ${totalHT.toFixed(2)} MAD`, 140, finalY);
    doc.text(`Total TVA (20%) : ${totalTVA.toFixed(2)} MAD`, 140, finalY + 6);
    doc.text(`Total T.T.C : ${totalTTC.toFixed(2)} MAD`, 140, finalY + 12);
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 255);
    doc.text(`Total T.T.C : ${totalTTC.toFixed(2)} MAD`, 14, finalY + 25);
    doc.save("devis_professionnel.pdf");
  };

  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const handleSendDevis = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSending) return;

    if (!clientName.trim()) {
      alert("Le nom du client est obligatoire.");
      return;
    }
    if (!numeroTele.trim()) {
      alert("Le numéro de téléphone est obligatoire.");
      return;
    }
    if (!email.trim()) {
      alert("L'adresse email est obligatoire.");
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        client: clientName,
        email: email,
        tele: numeroTele,
        Idmagasin: MagasinId,
        RefArticle: articles.map((a) => a.ref),
        designation: articles.map((a) => a.name),
        vendeur: articles.map((a) => a.vendeur || MagasinName || ""),
        qte: articles.map((a) => a.quantity),
        prix: articles.map((a) => a.price),
        status: "en cours",
        total: totalAllArticles.toFixed(2),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/magasin/${MagasinName}/devis/ajouter-direct`,
        payload
      );
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 2000);
    } catch (error) {
      console.error("Erreur lors de l'envoi du devis :", error);
      alert("Erreur lors de l'envoi du devis.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <NewSideBarMagasins />
      
      <div className="devis-container">
        {/* Hero Header */}
        <div className="devis-header">
          <div className="devis-header-content">
            <div className="devis-header-icon">
              <FileText size={40} strokeWidth={2} />
            </div>
            <div className="devis-header-text">
              <h1 className="devis-title">Nouveau Devis Client</h1>
              <p className="devis-subtitle">Créez directement un devis professionnel pour un client</p>
            </div>
          </div>
          <div className="devis-header-decoration"></div>
        </div>

        <form onSubmit={handleSendDevis}>
          {/* Client Information Card */}
          <div className="devis-card client-card">
            <div className="card-header-modern">
              <div className="header-icon-wrapper">
                <User size={20} strokeWidth={2.5} />
              </div>
              <h3 className="card-title-modern">Informations Client</h3>
            </div>
            <div className="card-body-modern" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              <div className="form-group-modern">
                <label className="form-label-modern">
                  <User size={16} strokeWidth={2.5} style={{ marginRight: '6px' }} />
                  Nom du Client
                </label>
                <input
                  type="text"
                  className="form-input-modern"
                  placeholder="Entrez le nom du client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group-modern">
                <label className="form-label-modern">
                  <Phone size={16} strokeWidth={2.5} style={{ marginRight: '6px' }} />
                  Numéro de Téléphone
                </label>
                <input
                  type="text"
                  className="form-input-modern"
                  placeholder="Entrez le numéro de téléphone"
                  value={numeroTele}
                  onChange={(e) => setNumeroTele(e.target.value)}
                  required
                />
              </div>

              <div className="form-group-modern">
                <label className="form-label-modern">
                  <Mail size={16} strokeWidth={2.5} style={{ marginRight: '6px' }} />
                  Adresse Email
                </label>
                <input
                  type="email"
                  className="form-input-modern"
                  placeholder="Entrez l'adresse email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Articles Section */}
          <div className="devis-card articles-card">
            <div className="card-header-modern">
              <div className="header-icon-wrapper">
                <Package size={20} strokeWidth={2.5} />
              </div>
              <h3 className="card-title-modern">Articles du Devis</h3>
              <div className="article-count-badge">
                {articles.length} {articles.length > 1 ? 'articles' : 'article'}
              </div>
            </div>

            <div className="card-body-modern">
              <div className="articles-list">
                {articles.map((article, index) => (
                  <div key={article.id} className="article-item">
                    <div className="article-number">
                      <span>{index + 1}</span>
                    </div>
                    
                    <div className="article-fields">
                      {/* Article Name with Autocomplete */}
                      <div className="field-wrapper field-name">
                        <label className="field-label">
                          <Package size={14} strokeWidth={2.5} />
                          Désignation
                        </label>
                        <div className="autocomplete-wrapper">
                          <input
                            type="text"
                            className="field-input"
                            placeholder="Rechercher un article..."
                            value={article.name}
                            onChange={(e) => {
                              handleChangeArticleValue(index, "name", e.target.value);
                              handleSearchInputChange(e);
                            }}
                            onFocus={() => {
                              setFocusedIndex(index);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                triggerSearch();
                              }
                            }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          />
                          <div className="field-icon" onClick={triggerSearch}>
                            <Search size={16} strokeWidth={2.5} />
                          </div>

                          {search !== '' && showSuggestions && focusedIndex === index && Refs.length > 0 && (
                            <div className="autocomplete-dropdown-modern">
                              {Refs.slice(0, 10).map((ref) => (
                                <div
                                  key={ref.id}
                                  className="autocomplete-item-modern"
                                  onClick={() => handleSelectArticle(index, ref)}
                                >
                                  <div className="autocomplete-item-header">
                                    <span className="autocomplete-item-name">{ref.Designation}</span>
                                    <span className="autocomplete-item-price">{(() => {
                                      const base = Number(String(ref.PrixNetHT).replace(",", ".")) || 0;
                                      const sameMag = ref.NomMagasin && MagasinName && ref.NomMagasin.toLowerCase() === MagasinName?.toLowerCase();
                                      const display = sameMag ? base : base * (1 + (globalCommissionPct / 100));
                                      return Number(display.toFixed(2));
                                    })()} MAD</span>
                                  </div>
                                  <span className="autocomplete-item-ref">Réf: {ref.RefArticle}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Quantity */}
                      <div className="field-wrapper field-quantity">
                        <label className="field-label">
                          <Calculator size={14} strokeWidth={2.5} />
                          Quantité
                        </label>
                        <input
                          type="number"
                          className="field-input"
                          placeholder="1"
                          min="1"
                          value={article.quantity}
                          onChange={(e) => handleChangeArticleValue(index, "quantity", e.target.value)}
                        />
                      </div>

                      {/* Price */}
                      <div className="field-wrapper field-price">
                        <label className="field-label">
                          <DollarSign size={14} strokeWidth={2.5} />
                          Prix Unitaire
                        </label>
                        <div className="price-input-wrapper">
                          <input
                            type="text"
                            className="field-input"
                            placeholder="0.00"
                            value={article.price}
                            onChange={(e) => handleChangeArticleValue(index, "price", e.target.value)}
                          />
                          <span className="currency-label">MAD</span>
                        </div>
                      </div>

                      {/* Subtotal */}
                      <div className="field-wrapper field-subtotal">
                        <label className="field-label">Sous-total</label>
                        <div className="subtotal-display">
                          <span className="subtotal-amount">{getTotalPerArticle(article).toFixed(2)}</span>
                          <span className="subtotal-currency">MAD</span>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    {articles.length > 1 && (
                      <button
                        type="button"
                        className="remove-article-btn"
                        onClick={() => handleRemoveArticle(index)}
                        title="Supprimer cet article"
                      >
                        <Trash2 size={18} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Article Button */}
              <button 
                type="button" 
                className="add-article-btn" 
                onClick={handleAddArticle}
              >
                <Plus size={20} strokeWidth={2.5} />
                <span>Ajouter un article</span>
              </button>
            </div>
          </div>

          {/* Summary Card */}
          <div className="devis-card summary-card">
            <div className="card-header-modern">
              <div className="header-icon-wrapper">
                <Calculator size={20} strokeWidth={2.5} />
              </div>
              <h3 className="card-title-modern">Récapitulatif du Devis</h3>
            </div>
            <div className="card-body-modern">
              <div className="summary-details">
                <div className="summary-divider"></div>
                <div className="summary-row summary-total">
                  <span className="summary-label">Total HT</span>
                  <span className="summary-value-total">{totalAllArticles.toFixed(2)} MAD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="devis-actions">
            <button 
              type="submit" 
              className="action-btn action-btn-send" 
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <Loader2 size={20} className="action-btn-spinner" strokeWidth={2.5} />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <Send size={20} strokeWidth={2.5} />
                  <span>Envoyer le Devis</span>
                </>
              )}
            </button>
            <button 
              type="button" 
              className="action-btn action-btn-download" 
              onClick={generatePDF}
            >
              <Download size={20} strokeWidth={2.5} />
              <span>Télécharger PDF</span>
            </button>
          </div>
        </form>

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="success-overlay">
            <div className="success-popup">
              <div className="success-icon-wrapper">
                <CheckCircle2 size={64} strokeWidth={2} />
              </div>
              <h3 className="success-title">Devis envoyé avec succès !</h3>
              <p className="success-message">Nous avons informé le client, il sera bientôt contacté.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateDevisDirect;
